import { html, nothing, type TemplateResult } from "lit";
import type { SavedCard } from "../../../data/live";
import type { GuestDetails } from "../../../payments/stripe-client";
import type { RequiredSignupFields } from "../../../config/types";
import {
  dateOfBirthError,
  resolveRequiredSignupFields,
  type RequiredProfileDraft,
} from "../../../checkout/required-profile";
import { formatShortAmount } from "../../format-money";

export type PaymentType = "card" | "apple-pay" | "google-pay" | "ach";

export interface PaymentSubmitViewModel {
  label?: string | undefined;
  busy: boolean;
  disabled: boolean;
  accent: string;
  total: number;
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
  onSubmit: () => void;
}

export function renderPaymentSubmit(
  model: PaymentSubmitViewModel,
): TemplateResult {
  return html`
    <button
      class="zpay-btn"
      type="button"
      ?disabled=${model.disabled}
      aria-busy=${model.busy ? "true" : "false"}
      style=${model.disabled ? "" : `background:${model.accent}`}
      @click=${model.onSubmit}
    >
      ${
        model.busy
          ? "Processing…"
          : (model.label ??
            `Pay ${formatShortAmount(model.total, model.currency)}`)
      }
    </button>
  `;
}

export interface PaymentTypeSelectorViewModel {
  selected: PaymentType;
  walletProbeDone: boolean;
  googlePay: boolean;
  applePay: boolean;
  /** ACH bank debit is offered (USD orgs only, matching the dashboard). */
  ach: boolean;
  onSelect: (type: PaymentType) => void;
}

export function renderPaymentTypeSelector(
  model: PaymentTypeSelectorViewModel,
): TemplateResult {
  const option = (
    type: PaymentType,
    label: string,
    description: string,
    icon: TemplateResult,
  ) => html`
    <button
      type="button"
      class="zpay-method ${model.selected === type ? "sel" : ""}"
      @click=${() => model.onSelect(type)}
    >
      <span class="ico" aria-hidden="true">${icon}</span>
      <span class="lbl"
        ><strong>${label}</strong><span>${description}</span></span
      >
    </button>
  `;

  return html`
    <div class="zpay-methods" role="listbox" aria-label="Payment method">
      ${!model.walletProbeDone
        ? html`<div class="zpay-wallet-hint">
            Checking wallet availability…
          </div>`
        : nothing}
      ${option(
        "card",
        "Credit / Debit Card",
        "Pay securely with your card",
        html`<svg
          class="ico"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
          />
        </svg>`,
      )}
      ${model.googlePay
        ? option(
            "google-pay",
            "Google Pay",
            "Fast checkout with Google Pay",
            html`<svg class="ico" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"
              />
            </svg>`,
          )
        : nothing}
      ${model.applePay
        ? option(
            "apple-pay",
            "Apple Pay",
            "Fast checkout with Apple Pay",
            html`<svg class="ico" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
              />
            </svg>`,
          )
        : nothing}
      ${model.ach
        ? option(
            "ach",
            "Bank account (ACH)",
            "Pay directly from your bank account",
            bankIcon,
          )
        : nothing}
    </div>
  `;
}

const bankIcon = html`<svg
  class="ico"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
>
  <path
    stroke-linecap="round"
    stroke-linejoin="round"
    d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
  />
</svg>`;

export interface SavedCardPickerViewModel {
  cards: readonly SavedCard[];
  selectedCardId: string;
  /** Wallet in use instead of a card, if any. */
  selectedWallet: PaymentType | null;
  applePay: boolean;
  googlePay: boolean;
  /** Offer connecting a bank account (ACH) instead of a card on file. */
  ach: boolean;
  onSelect: (paymentMethodId: string) => void;
  onSelectWallet: (type: "apple-pay" | "google-pay" | "ach") => void;
}

export function renderSavedCardPicker(
  model: SavedCardPickerViewModel,
): TemplateResult {
  const capitalize = (value: string) =>
    value ? value[0].toUpperCase() + value.slice(1) : "Card";
  const button = (selected: boolean, label: string, onClick: () => void) =>
    html`
      <button
        type="button"
        class="zpay-card-opt ${selected ? "sel" : ""}"
        @click=${onClick}
      >
        <span class="zpay-radio"></span><span>${label}</span>
      </button>
    `;
  const option = (id: string, label: string) =>
    button(
      !model.selectedWallet && model.selectedCardId === id,
      label,
      () => model.onSelect(id),
    );
  const wallet = (type: "apple-pay" | "google-pay" | "ach", label: string) =>
    button(model.selectedWallet === type, label, () =>
      model.onSelectWallet(type),
    );
  return html`
    <div class="zpay-cards">
      ${model.cards.map((card) =>
        option(
          card.paymentMethodId,
          `${capitalize(card.brand)} •••• ${card.last4}${
            card.isDefault ? " · Default" : ""
          }`,
        ),
      )}
      ${model.applePay ? wallet("apple-pay", "Apple Pay") : nothing}
      ${model.googlePay ? wallet("google-pay", "Google Pay") : nothing}
      ${model.ach ? wallet("ach", "Connect a bank account (ACH)") : nothing}
      ${option("", "Use a new card")}
    </div>
  `;
}

export interface BankAccountFormViewModel {
  accountHolderName: string;
  onNameChange: (value: string) => void;
}

/**
 * ACH via Stripe Financial Connections, mirroring the dashboard
 * `ACHPaymentForm`: the buyer only names the account holder here. The bank
 * itself is chosen and verified in Stripe's own modal, which opens when the
 * host's primary button submits.
 */
export function renderBankAccountForm(
  model: BankAccountFormViewModel,
): TemplateResult {
  return html`
    <p class="zpay-note">
      Connect your bank account securely through Stripe. It is saved for
      future payments.
    </p>
    <label class="zpay-field-label" for="zpay-ach-name">
      Account holder name
    </label>
    <input
      id="zpay-ach-name"
      class="zpay-input"
      placeholder="Full name on bank account"
      autocomplete="name"
      .value=${model.accountHolderName}
      @input=${(event: Event) =>
        model.onNameChange((event.target as HTMLInputElement).value)}
    />
    <p class="zpay-total-note">
      By connecting your bank account, you authorize charges to be debited from
      your account. ACH payments typically take 3-5 business days.
    </p>
  `;
}

export interface PaymentCustomerDetailsViewModel {
  draft: GuestDetails & { phone?: string };
  requirePhone: boolean;
  showPhone: boolean;
  invalidPhone: boolean;
  effectivePhone: string;
  onChange: (
    field: "firstName" | "lastName" | "email" | "phone",
    value: string,
  ) => void;
  /** Org's required-signup-fields policy; omitted means nothing extra. */
  requiredSignupFields?: RequiredSignupFields | undefined;
  requiredProfile?: RequiredProfileDraft | undefined;
  onRequiredProfileChange?:
    ((field: keyof RequiredProfileDraft, value: string) => void) | undefined;
}

export function renderPaymentCustomerDetails(
  model: PaymentCustomerDetailsViewModel,
): TemplateResult {
  const input =
    (field: "firstName" | "lastName" | "email" | "phone") => (event: Event) =>
      model.onChange(field, (event.target as HTMLInputElement).value);
  return html`
    <div class="zpay-row">
      <input
        class="zpay-input"
        placeholder="First name"
        .value=${model.draft.firstName}
        @input=${input("firstName")}
      />
      <input
        class="zpay-input"
        placeholder="Last name"
        .value=${model.draft.lastName}
        @input=${input("lastName")}
      />
    </div>
    <input
      class="zpay-input"
      type="email"
      placeholder="Email"
      .value=${model.draft.email}
      @input=${input("email")}
    />
    ${model.showPhone
      ? renderPaymentPhoneField({
          required: model.requirePhone,
          value: model.effectivePhone,
          onChange: (value) => model.onChange("phone", value),
        })
      : nothing}
    ${model.invalidPhone
      ? html`<p role="alert" class="zpay-err">Enter a valid phone number; include + and a country code if needed.</p>`
      : nothing}
    ${model.requiredProfile && model.onRequiredProfileChange
      ? renderRequiredProfileFields({
          fields: model.requiredSignupFields,
          draft: model.requiredProfile,
          onChange: model.onRequiredProfileChange,
        })
      : nothing}
  `;
}

export interface RequiredProfileFieldsViewModel {
  fields: RequiredSignupFields | undefined;
  draft: RequiredProfileDraft;
  onChange: (field: keyof RequiredProfileDraft, value: string) => void;
}

/**
 * The extra profile fields an org can mark required at signup (PLAT-4456).
 *
 * Rendered wherever the embed collects a new customer's details, because guest
 * checkout creates the roster itself and the API re-checks this policy — an
 * embed that skips these fields fails at `create-guest`, after the customer has
 * filled in everything else.
 */
export function renderRequiredProfileFields(
  model: RequiredProfileFieldsViewModel,
): TemplateResult | typeof nothing {
  const required = resolveRequiredSignupFields(model.fields);
  const dobError = model.draft.dateOfBirth
    ? dateOfBirthError(model.fields, model.draft.dateOfBirth)
    : null;
  if (!required.dateOfBirth && !required.emergencyContact) return nothing;
  const input = (field: keyof RequiredProfileDraft) => (event: Event) =>
    model.onChange(field, (event.target as HTMLInputElement).value);
  return html`
    ${required.dateOfBirth
      ? html`<label class="zpay-field-label" for="zpay-dob">
            Date of birth (required)
          </label>
          <input
            id="zpay-dob"
            class="zpay-input"
            type="date"
            required
            .value=${model.draft.dateOfBirth}
            @input=${input("dateOfBirth")}
          />`
      : nothing}
    ${dobError ? html`<p role="alert" style="color:#b91c1c;font-size:0.8rem">${dobError}</p>` : nothing}
    ${required.emergencyContact
      ? html`<div class="zpay-row">
          <input
            class="zpay-input"
            type="text"
            required
            placeholder="Emergency contact name (required)"
            .value=${model.draft.emergencyContactName}
            @input=${input("emergencyContactName")}
          />
          <input
            class="zpay-input"
            type="tel"
            inputmode="tel"
            required
            placeholder="Emergency contact phone (required)"
            .value=${model.draft.emergencyContactPhone}
            @input=${input("emergencyContactPhone")}
          />
        </div>`
      : nothing}
  `;
}

export interface PaymentPhoneFieldViewModel {
  required: boolean;
  value: string;
  onChange: (value: string) => void;
}

export function renderPaymentPhoneField(
  model: PaymentPhoneFieldViewModel,
): TemplateResult {
  return html`
    <input
      class="zpay-input"
      type="tel"
      inputmode="tel"
      ?required=${model.required}
      placeholder="Phone number${model.required ? " (required)" : ""}"
      .value=${model.value}
      @input=${(event: Event) =>
        model.onChange((event.target as HTMLInputElement).value)}
    />
  `;
}

export interface PaymentUnavailableViewModel {
  stripeConfigured: boolean;
  inline: boolean;
  accent: string;
  onRetry: () => void;
  onClose: () => void;
}

export function renderPaymentUnavailable(
  model: PaymentUnavailableViewModel,
): TemplateResult {
  const reason = model.stripeConfigured
    ? "This item isn’t available for online purchase yet."
    : "Online payment isn’t set up for this studio yet.";
  const next = model.stripeConfigured
    ? "This is usually temporary — try again in a moment, or contact the studio to book directly."
    : "Please contact the studio to book and pay directly.";
  return html`
    <p class="zpay-note">${reason}</p>
    <p class="zpay-note">${next}</p>
    ${model.stripeConfigured
      ? html`<button
          class="zpay-btn"
          type="button"
          style=${`background:${model.accent}`}
          @click=${model.onRetry}
        >
          Try again
        </button>`
      : nothing}
    ${model.inline
      ? nothing
      : html`<button class="zpay-link" @click=${model.onClose}>Close</button>`}
  `;
}
